import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../config/firebase";
import { collection, getDocs, query, orderBy, onSnapshot } from "firebase/firestore";
import { cancelOrder, retryOrder, isSyncLocked } from "../services/orderService";
import { sortOrderItems } from "../utils/orderSort";
import { getAllProducts, getCustomerRecords } from "../services/dataCache";
import OrderSheetModal from "../components/OrderSheetModal";

const SYNC_LOCK_MSG = "A QuickBooks sync session has picked this order up — cancel is disabled. Handle any changes in QuickBooks after the sync.";

// Any order that isn't cancelled can be ticked for bulk printing — a pending
// order prints with "Invoice #: pending", exactly like its single sheet.
const printable = (o) => o.status !== "cancelled";

export default function AdminOrders({ onEditToCart }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState({});
  const [customerRecords, setCustomerRecords] = useState({}); // record id -> record
  const [products, setProducts] = useState({}); // sku -> product data (name, storageMethod)
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // order id currently expanded
  const [sheetOrder, setSheetOrder] = useState(null); // order in the 📋 popup
  // Bulk packing-list printing (Brian, 2026-10-13): tick orders — or a whole
  // day via the divider row — and print every sheet in one go at /packing-bulk.
  const [selected, setSelected] = useState(() => new Set());

  // Staff edit-any-order (Brian, 2026-08-24): cancel the pending order
  // (stock restored) and rebuild it FAITHFULLY in the staff cart — returns,
  // credits, price overrides, free goods, and line notes all survive, and
  // the store it was for is pre-selected — fix one line, place it again.
  async function handleEdit(o) {
    if (!window.confirm(
      `Edit ${o.orderNumber} (${customerLabel(o)})?\n\nThis CANCELS the order (stock restored) and rebuilds it in YOUR cart — anything currently in your cart is replaced. Review, fix, and place it again.`)) return;
    setBusy(o.id);
    try {
      await cancelOrder(o.id);
      const rebuilt = [];
      let skipped = 0;
      for (const item of o.items || []) {
        if (item.creditLine) {
          rebuilt.push({
            sku: "10000", name: "Credit", creditLine: true,
            creditDesc: item.creditDesc || "", qty: 1,
            priceAtOrder: item.priceAtOrder, basePrice: item.priceAtOrder,
            cartKey: `credit-${Date.now()}-${rebuilt.length}`
          });
          continue;
        }
        const p = products[item.sku];
        if (!p) { skipped++; continue; } // product no longer exists
        if (item.freeGoods) {
          // fold the $0 line back into its paid line's Free-units field
          const host = rebuilt.find(x => x.sku === item.sku && !x.isReturn && !x.creditLine);
          if (host) host.freeQty = (host.freeQty || 0) + item.qty;
          else rebuilt.push({ ...p, qty: item.qty, priceOverride: 0 });
          continue;
        }
        const line = { ...p, qty: item.qty };
        if (item.qty < 0) { line.isReturn = true; line.cartKey = `${item.sku}:ret`; }
        if (item.customPrice) line.priceOverride = item.priceAtOrder;
        if (item.lineNote) line.lineNote = item.lineNote;
        rebuilt.push(line);
      }
      if (skipped > 0) {
        alert(`${skipped} item(s) no longer exist in the product list and were skipped — check the rebuilt order.`);
      }
      const rec = o.customerRecordId ? customerRecords[o.customerRecordId] : null;
      const orderForSel = rec
        ? { type: "record", id: rec.id, name: rec.name, linkedUserId: rec.linkedUserId || null }
        : null;
      onEditToCart(rebuilt, orderForSel, o.comment || "", o.keepWrittenOrder === true);
      if (!rec) {
        alert("This order has no store record attached — pick the customer in the cart before placing it again.");
      }
      navigate("/cart");
    } catch (err) {
      alert(err.message);
      setBusy(null);
    }
  }

  // Rows for the shared order-sheet popup (list ↔ photos) — same shape as
  // My Orders / Cart use.
  function sheetRowsFor(o) {
    const toRow = (item, idx) => ({
      key: `${idx}:${item.sku}`,
      sku: item.sku,
      name: item.creditLine ? null : (products[item.sku]?.name || item.sku),
      qty: item.qty,
      imageUrl: products[item.sku]?.imageUrl || null,
      isReturn: item.qty < 0,
      creditLine: item.creditLine,
      creditDesc: item.creditDesc,
      freeGoods: item.freeGoods
    });
    const { sorted } = sortedItemsFor(o);
    return { rows: sorted.map(toRow), originalRows: (o.items || []).map(toRow) };
  }
  const [busy, setBusy] = useState(null);

  // No manual state patching after retry/cancel: the orders listener below
  // reflects the write immediately, and also shows status changes made by the
  // QB sync server or another machine without a page reload.
  async function handleRetry(o) {
    setBusy(o.id);
    try {
      await retryOrder(o.id);
    } catch (err) { alert(err.message); }
    finally { setBusy(null); }
  }

  async function handleCancel(o) {
    if (!window.confirm(`Cancel order ${o.orderNumber} and restore its stock?`)) return;
    setBusy(o.id);
    try {
      await cancelOrder(o.id);
    } catch (err) { alert(err.message); }
    finally { setBusy(null); }
  }

  useEffect(() => {
    // Users / customer records / products are loaded once (for name and
    // storage-method lookups); orders stream live. Products + customer
    // records come from the session cache — 0 reads on revisit.
    getDocs(collection(db, "users")).then(snap => {
      setUsers(Object.fromEntries(snap.docs.map(d => [d.id, d.data()])));
    });
    getCustomerRecords().then(recs => {
      setCustomerRecords(Object.fromEntries(recs.map(r => [r.id, r])));
    });
    getAllProducts().then(prods => {
      setProducts(Object.fromEntries(prods.map(p => [p.sku, p])));
    });
    const unsubscribe = onSnapshot(
      query(collection(db, "orders"), orderBy("createdAt", "desc")),
      snap => {
        setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  // Prefer the customer RECORD name for orders placed on behalf of a record
  // (rep/admin ordering); fall back to the portal account for self-placed orders.
  function customerLabel(o) {
    if (o.customerRecordId) {
      const r = customerRecords[o.customerRecordId];
      if (r) return r.name;
    }
    const u = users[o.customerId];
    if (!u) return "Unknown";
    return u.companyName || u.name || u.email || "Unknown";
  }

  function formatDate(ts) {
    if (!ts || !ts.toDate) return "—";
    return ts.toDate().toLocaleString("en-CA", {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit"
    });
  }

  // Date group dividers (Brian, 2026-09-01): the list is createdAt-desc, so
  // a header row wherever the calendar day changes splits it into per-day
  // sections at a glance.
  function dayKey(ts) {
    return ts && ts.toDate ? ts.toDate().toDateString() : "unknown";
  }
  function dayLabel(ts) {
    if (!ts || !ts.toDate) return "Unknown date";
    return ts.toDate().toLocaleDateString("en-CA", {
      weekday: "long", year: "numeric", month: "short", day: "numeric"
    });
  }

  // Sort an order's items into canonical output order using live product data,
  // so the on-screen detail matches the packing sheet and the QB invoice.
  function sortedItemsFor(order) {
    const storageBySku = Object.fromEntries(
      Object.entries(products).map(([sku, p]) => [sku, p.storageMethod || ""])
    );
    return sortOrderItems(order.items || [], storageBySku);
  }

  // ---- bulk selection ----
  function setSelection(fn) {
    setSelected(prev => { const next = new Set(prev); fn(next); return next; });
  }
  function toggleOne(id) {
    setSelection(s => { if (s.has(id)) s.delete(id); else s.add(id); });
  }
  // Day divider checkbox: tick every printable order of that day, or clear
  // them all if they are already all ticked.
  function toggleDay(dayOrders) {
    const ids = dayOrders.map(o => o.id);
    const all = ids.length > 0 && ids.every(id => selected.has(id));
    setSelection(s => ids.forEach(id => (all ? s.delete(id) : s.add(id))));
  }
  function toggleAll() {
    const ids = orders.filter(printable).map(o => o.id);
    const all = ids.length > 0 && ids.every(id => selected.has(id));
    setSelection(s => ids.forEach(id => (all ? s.delete(id) : s.add(id))));
  }
  function clearSelection() { setSelected(new Set()); }
  function printSelected() {
    // keep the list's order (newest first); the bulk page auto-opens the print dialog
    const ids = orders.filter(o => selected.has(o.id)).map(o => o.id);
    if (ids.length === 0) return;
    navigate(`/packing-bulk?ids=${ids.join(",")}&print=1`);
  }

  if (loading) return <p>Loading orders...</p>;
  if (orders.length === 0) return <div className="admin-orders"><h2>Orders</h2><p>No orders yet.</p></div>;

  const printableIds = orders.filter(printable).map(o => o.id);
  const allSelected = printableIds.length > 0 && printableIds.every(id => selected.has(id));
  const selectedCount = printableIds.filter(id => selected.has(id)).length;

  return (
    <div className="admin-orders">
      <h2>Orders ({orders.length})</h2>
      {selectedCount > 0 && (
        <div className="bulk-bar no-print">
          <strong>{selectedCount} order{selectedCount === 1 ? "" : "s"} selected</strong>
          <button className="print-btn" onClick={printSelected}>🖨 Print packing lists</button>
          <button className="clear-selection-btn" onClick={clearSelection}>Clear</button>
        </div>
      )}
      <table>
        <thead>
          <tr>
            <th className="select-col">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                title="Select every order that can be printed"
                aria-label="Select all orders"
              />
            </th>
            <th>Order #</th><th>Date</th><th>Customer</th>
            <th>Items</th><th>Total</th><th>QB Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o, i) => {
            const locked = isSyncLocked(o);
            const newDay = i === 0 || dayKey(o.createdAt) !== dayKey(orders[i - 1].createdAt);
            const dayOrders = newDay
              ? orders.filter(x => dayKey(x.createdAt) === dayKey(o.createdAt) && printable(x))
              : [];
            const dayAll = dayOrders.length > 0 && dayOrders.every(x => selected.has(x.id));
            return (
              <Fragment key={o.id}>
                {newDay && (
                  <tr className="date-divider-row">
                    <td colSpan={8}>
                      <label className="day-select" title="Select every order of this day">
                        <input
                          type="checkbox"
                          checked={dayAll}
                          disabled={dayOrders.length === 0}
                          onChange={() => toggleDay(dayOrders)}
                        />
                        {dayLabel(o.createdAt)}
                        <span className="day-count">({dayOrders.length})</span>
                      </label>
                    </td>
                  </tr>
                )}
                <tr className="clickable order-row"
                    onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                  <td className="select-col" onClick={(e) => e.stopPropagation()}>
                    {printable(o) && (
                      <input
                        type="checkbox"
                        checked={selected.has(o.id)}
                        onChange={() => toggleOne(o.id)}
                        aria-label={`Select order ${o.orderNumber}`}
                      />
                    )}
                  </td>
                  <td>
                    {o.orderNumber}
                    {o.comment && <span className="comment-badge" title={o.comment}>💬</span>}
                  </td>
                  <td>{formatDate(o.createdAt)}</td>
                  <td>{customerLabel(o)}</td>
                  <td>{(o.items || []).length}</td>
                  <td>${(o.totalAmount || 0).toFixed(2)}</td>
                  <td>
                    <span className={
                      o.status === "synced" ? "status-synced" :
                      o.status === "error" ? "status-error" :
                      o.status === "cancelled" ? "status-cancelled" : "status-pending"
                    }>
                      {o.status === "pending_sync" ? (locked ? "Syncing to QB…" : "Waiting for QB") :
                       o.status === "synced" ? "In QuickBooks" :
                       o.status === "cancelled" ? "Cancelled" : "Error"}
                    </span>
                    {o.status === "error" && o.syncError && (
                      <div className="sync-error-detail">{o.syncError}</div>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button className="mini-btn" title="Order sheet (list / photos)" onClick={() => setSheetOrder(o)}>📋</button>
                    {o.status === "error" && (
                      <button className="mini-btn" disabled={busy === o.id} onClick={() => handleRetry(o)}>Retry</button>
                    )}
                    {(o.status === "pending_sync" || o.status === "error") && (
                      <>
                        <button
                          className="mini-btn"
                          disabled={busy === o.id || locked}
                          title={locked ? SYNC_LOCK_MSG : "Cancel & rebuild this order in your cart to fix it"}
                          onClick={() => handleEdit(o)}
                        >Edit</button>
                        <button
                          className="mini-btn danger"
                          disabled={busy === o.id || locked}
                          title={locked ? SYNC_LOCK_MSG : undefined}
                          onClick={() => handleCancel(o)}
                        >Cancel</button>
                      </>
                    )}
                    {o.status === "synced" && (
                      <Link className="mini-btn" to={`/packing/${o.id}`}>🖨 Packing Sheet</Link>
                    )}
                  </td>
                </tr>
                {expanded === o.id && (() => {
                  const { sorted, missingStorageSkus: rawMissing } = sortedItemsFor(o);
                  // Credit lines (item 10000) aren't products — keep them out
                  // of the "no storage method" warning.
                  const missingStorageSkus = rawMissing.filter(sku =>
                    sorted.some(i => String(i.sku) === sku && !i.creditLine));
                  return (
                    <tr>
                      <td colSpan={8}>
                        {o.comment && (
                          <div className="order-comment-box">
                            💬 <strong>Customer note:</strong> {o.comment}
                          </div>
                        )}
                        {missingStorageSkus.length > 0 && (
                          <div className="storage-warning-banner">
                            ⚠ These SKUs have no storage method set and were sorted last:{" "}
                            <strong>{missingStorageSkus.join(", ")}</strong>. Set their
                            storage method on the Products page so they group correctly.
                          </div>
                        )}
                        <table className="order-items-table">
                          <thead>
                            <tr><th>SKU</th><th>Product</th><th>Storage</th><th>Qty</th><th>Price</th><th>Subtotal</th></tr>
                          </thead>
                          <tbody>
                            {sorted.map((item, idx) => {
                              const p = products[String(item.sku)];
                              const noStorage = missingStorageSkus.includes(String(item.sku));
                              return (
                                <tr key={idx} className={noStorage ? "no-storage-row" : ""}>
                                  <td>{item.sku}</td>
                                  <td>
                                    {item.qty < 0 && <span className="return-tag">↩ RETURN </span>}
                                    {item.freeGoods && <span className="free-tag">🎁 FREE </span>}
                                    {item.creditLine
                                      ? <>Credit — {item.creditDesc}</>
                                      : (p?.name || "(unknown product)")}
                                    {item.lineNote && (
                                      <div className="line-note">📝 {item.lineNote}</div>
                                    )}
                                  </td>
                                  <td>{item.creditLine ? "—" : (p?.storageMethod || "—")}</td>
                                  <td>{item.qty}</td>
                                  <td>${item.priceAtOrder.toFixed(2)}</td>
                                  <td>${(item.qty * item.priceAtOrder).toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  );
                })()}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <p className="inventory-help-note">
        Click any order row to see its items. Tick orders — or a whole day on its divider — and press
        <strong> Print packing lists</strong> to print them all at once; the 🖨 button prints one.
      </p>
      {sheetOrder && (
        <OrderSheetModal
          title={sheetOrder.orderNumber}
          customerName={customerLabel(sheetOrder)}
          dateLabel={formatDate(sheetOrder.createdAt)}
          {...sheetRowsFor(sheetOrder)}
          onClose={() => setSheetOrder(null)}
        />
      )}
    </div>
  );
}
